/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Shared file handling utilities.
 * Provides common functionality for file processing across frontend and backend.
 */

/**
 * File type categories and their associated MIME types.
 * Single source of truth for supported file types.
 */
export const SUPPORTED_FILE_TYPES = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  pdf: ['application/pdf'],
  text: ['text/plain', 'text/markdown', 'text/csv', 'text/html'],
  document: [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.oasis.opendocument.text'
  ],
  spreadsheet: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.oasis.opendocument.spreadsheet'
  ],
  audio: ['audio/mpeg', 'audio/wav', 'audio/webm', 'audio/ogg'],
  video: ['video/mp4', 'video/webm', 'video/ogg']
} as const;

/**
 * Maximum file sizes by category (in bytes)
 */
export const MAX_FILE_SIZES = {
  image: 20 * 1024 * 1024,      // 20MB
  pdf: 25 * 1024 * 1024,        // 25MB
  text: 10 * 1024 * 1024,       // 10MB
  document: 25 * 1024 * 1024,   // 25MB
  spreadsheet: 25 * 1024 * 1024, // 25MB
  audio: 25 * 1024 * 1024,      // 25MB
  video: 100 * 1024 * 1024,     // 100MB
  default: 10 * 1024 * 1024     // 10MB
} as const;

export type FileCategory = keyof typeof SUPPORTED_FILE_TYPES;

/**
 * Gets the category of a file based on its MIME type.
 * Returns null if the file type is not supported.
 */
export function getFileCategory(mimeType: string): FileCategory | null {
  if (!mimeType) return null;

  for (const [category, types] of Object.entries(SUPPORTED_FILE_TYPES)) {
    if (types.some(type => mimeType.startsWith(type))) {
      return category as FileCategory;
    }
  }

  return null;
}

/**
 * Checks if a MIME type is supported.
 */
export function isSupportedFileType(mimeType: string): boolean {
  return getFileCategory(mimeType) !== null;
}

/**
 * Gets the maximum file size for a given MIME type.
 */
export function getMaxFileSize(mimeType: string): number {
  const category = getFileCategory(mimeType);
  return category ? MAX_FILE_SIZES[category] : MAX_FILE_SIZES.default;
}

/**
 * Validates file size based on its type.
 * Returns validation result with error message if invalid.
 */
export interface FileSizeValidation {
  isValid: boolean;
  maxSize: number;
  actualSize: number;
  error?: string;
}

export function validateFileSize(
  fileSize: number,
  mimeType: string
): FileSizeValidation {
  const maxSize = getMaxFileSize(mimeType);
  const isValid = fileSize <= maxSize;

  return {
    isValid,
    maxSize,
    actualSize: fileSize,
    error: isValid
      ? undefined
      : `File size ${formatFileSize(fileSize)} exceeds maximum allowed size of ${formatFileSize(maxSize)}`
  };
}

/**
 * Formats file size to human-readable string.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Gets a simplified file type from MIME type for display purposes.
 */
export function getSimplifiedFileType(mimeType: string): string {
  const category = getFileCategory(mimeType);
  return category || 'file';
}

/**
 * Common file metadata interface
 */
export interface FileMetadata {
  name: string;
  mimeType: string;
  size: number;
  category: FileCategory | null;
  isSupported: boolean;
}

/**
 * Extracts standardized metadata from various file objects.
 * Handles different property names across frontend and backend.
 */
export function extractFileMetadata(file: any): FileMetadata {
  // Try different property names for file metadata
  const name = file.name || file.file_name || file.fileName || 'unnamed';
  const mimeType = file.mimeType || file.file_mime || 'application/octet-stream';
  const size = file.size || file.file_size || file.fileSize || 0;

  const category = getFileCategory(mimeType);

  return {
    name,
    mimeType,
    size,
    category,
    isSupported: isSupportedFileType(mimeType)
  };
}

/**
 * Checks if a file is an image based on MIME type.
 */
export function isImageFile(mimeType: string): boolean {
  return getFileCategory(mimeType) === 'image';
}

/**
 * Checks if a file is a text-based file (including PDFs and documents).
 */
export function isTextBasedFile(mimeType: string): boolean {
  const category = getFileCategory(mimeType);
  return category === 'text' || category === 'pdf' || category === 'document';
}

/**
 * Gets the appropriate file purpose for AI processing.
 * Used to determine how a file should be handled by AI providers.
 */
export function getAIFilePurpose(mimeType: string): 'vision' | 'text' | 'unsupported' {
  if (isImageFile(mimeType)) {
    return 'vision';
  }
  if (isTextBasedFile(mimeType)) {
    return 'text';
  }
  return 'unsupported';
}